import React, { useState, useEffect } from 'react';
import { AiOutlineEdit, AiOutlineDelete, AiOutlinePlus, AiOutlineSave, AiOutlineClose, AiOutlineHistory, AiOutlineCheck, AiOutlineStop } from 'react-icons/ai';
import { useRetentionPolicyService, useDocumentTypeService } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { useConfirm } from '../components/ui/ConfirmProvider';

const getRetentionPartsFromDays = (days) => {
  if (!days || Number.isNaN(Number(days))) {
    return { retentionPeriodValue: '', retentionPeriodUnit: 'd' };
  }

  const totalDays = Number(days);

  if (totalDays % 365 === 0) {
    return { retentionPeriodValue: String(totalDays / 365), retentionPeriodUnit: 'y' };
  }

  if (totalDays % 30 === 0) {
    return { retentionPeriodValue: String(totalDays / 30), retentionPeriodUnit: 'm' };
  }

  return { retentionPeriodValue: String(totalDays), retentionPeriodUnit: 'd' };
};

export default function AdminRetentionPoliciesPage({ onBack }) {
  const retentionService = useRetentionPolicyService();
  const documentTypeService = useDocumentTypeService();
  const notify = useNotification();
  const confirm = useConfirm();

  const [policies, setPolicies] = useState([]);
  const [documentSeries, setDocumentSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    idPolicy: '',
    name: '',
    retentionPeriodValue: '',
    retentionPeriodUnit: 'd',
    retentionStartEvent: 'PUBLISHED',
    requiresApproval: false,
    physicalDelete: false,
    active: true
  });

  // Load policies and series
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Admin de políticas: necesita listar TODAS las series para asociar/desasociar.
      const [policiesRes, seriesRes] = await Promise.all([
        retentionService.getPolicies(),
        documentTypeService.getTiposDocumentos({ scope: 'all' })
      ]);
      
      const policiesData = policiesRes.data || [];
      const seriesData = Array.isArray(seriesRes.data) ? seriesRes.data : (seriesRes.data?.items || []);
      
      // Need to fetch which policy is associated to which series
      // For now we'll do this serially, but we can map it
      const seriesByPolicy = {};
      
      // We will initialize the empty arrays for all policies
      policiesData.forEach(p => {
        seriesByPolicy[p.idPolicy] = [];
      });

      // Get association mapping
      for (const serie of seriesData) {
        try {
          const assocRes = await retentionService.getPoliciesByTipoDoc(serie.id);
          const activePolicies = assocRes.data || [];
          activePolicies.forEach(ap => {
            if (seriesByPolicy[ap.idPolicy]) {
              seriesByPolicy[ap.idPolicy].push(serie);
            } else {
              seriesByPolicy[ap.idPolicy] = [serie];
            }
          });
        } catch (e) {
          console.warn(`Could not get policy for series ${serie.id}`);
        }
      }

      // Add associated series to the policy objects
      const enhancedPolicies = policiesData.map(p => ({
        ...p,
        associatedSeries: seriesByPolicy[p.idPolicy] || []
      }));

      setPolicies(enhancedPolicies);
      setDocumentSeries(seriesData);
    } catch (err) {
      setError('Error cargando políticas de retención: ' + err.message);
      notify.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setFormData({
      idPolicy: '',
      name: '',
      retentionPeriodValue: '',
      retentionPeriodUnit: 'd',
      retentionStartEvent: 'PUBLISHED',
      requiresApproval: false,
      physicalDelete: false,
      active: true
    });
    setSelectedPolicy('new');
    setIsEditing(true);
  };

  const handleSelectPolicy = (policy) => {
    const retentionParts = getRetentionPartsFromDays(policy.retentionPeriodDays);

    setSelectedPolicy(policy.idPolicy);
    setFormData({
      idPolicy: policy.idPolicy,
      name: policy.name,
      retentionPeriodValue: retentionParts.retentionPeriodValue,
      retentionPeriodUnit: retentionParts.retentionPeriodUnit,
      retentionStartEvent: policy.retentionStartEvent || 'PUBLISHED',
      requiresApproval: policy.requiresApproval || false,
      physicalDelete: policy.physicalDelete || false,
      active: policy.active ?? true
    });
    setIsEditing(true);
  };

  const handleToggleActive = async (policy, e) => {
    e.stopPropagation();
    try {
      if (policy.active) {
        await retentionService.deactivatePolicy(policy.idPolicy);
        notify.success('Política desactivada');
      } else {
        await retentionService.activatePolicy(policy.idPolicy);
        notify.success('Política activada');
      }
      fetchData();
    } catch (err) {
      notify.error('Error cambiando estado de la política');
    }
  };

  const handleDelete = async (policy, e) => {
    e.stopPropagation();
    const ok = await confirm({
      title: 'Eliminar política',
      message: `¿Estás seguro de eliminar la política '${policy.name}'?`,
      confirmLabel: 'Eliminar',
      destructive: true,
    });
    if (ok) {
      try {
        await retentionService.deletePolicy(policy.idPolicy);
        notify.success('Política eliminada');
        if (selectedPolicy === policy.idPolicy) {
          setSelectedPolicy(null);
          setIsEditing(false);
        }
        fetchData();
      } catch (err) {
        notify.error('Error eliminando la política');
      }
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.name) {
        notify.error('El nombre es obligatorio');
        return;
      }
      
      const payload = {
        name: formData.name,
        retentionPeriod: '',
        retentionStartEvent: formData.retentionStartEvent,
        requiresApproval: formData.requiresApproval,
        physicalDelete: formData.physicalDelete,
        active: formData.active
      };

      const valueNumber = Number(formData.retentionPeriodValue);
      if (!formData.retentionPeriodValue || Number.isNaN(valueNumber) || valueNumber <= 0) {
        notify.error('Debe ingresar un valor numérico mayor a 0 para el período');
        return;
      }
      payload.retentionPeriod = `${valueNumber}${formData.retentionPeriodUnit}`;

      if (selectedPolicy === 'new') {
        await retentionService.createPolicy(payload);
        notify.success('Política creada exitosamente');
      } else {
        await retentionService.updatePolicy(formData.idPolicy, payload);
        notify.success('Política actualizada exitosamente');
      }
      
      setSelectedPolicy(null);
      setIsEditing(false);
      fetchData();
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.errors) {
         err.response.data.errors.forEach(e => notify.error(e.message));
      } else {
         notify.error('Error al guardar: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.titleSection}>
          <AiOutlineHistory style={styles.titleIcon} />
          <h1 style={styles.title}>Políticas de Retención</h1>
        </div>
        <button style={styles.backButton} onClick={onBack || (() => window.history.back())}>
          <AiOutlineClose style={styles.backIcon} />
          Cerrar
        </button>
      </div>

      {error && <div style={styles.errorMessage}>{error}</div>}

      <div style={styles.content}>
        <div style={styles.listSection}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Políticas Existentes</h2>
            <button style={styles.addButton} onClick={handleCreateNew}>
              <AiOutlinePlus /> Nueva Política
            </button>
          </div>

          {loading ? (
            <div style={{ padding: 20 }}>Cargando...</div>
          ) : (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Nombre</th>
                    <th style={styles.th}>Retención</th>
                    <th style={styles.th}>Series Asociadas</th>
                    <th style={styles.th}>Estado</th>
                    <th style={styles.th}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {policies.map(p => (
                    <tr 
                      key={p.idPolicy} 
                      style={{...styles.tr, ...(selectedPolicy === p.idPolicy ? styles.trSelected : {})}}
                      onClick={() => handleSelectPolicy(p)}
                    >
                      <td style={styles.td}>{p.name}</td>
                      <td style={styles.td}>{p.retentionPeriodDays} días</td>
                      <td style={styles.td}>
                        {p.associatedSeries?.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {p.associatedSeries.map(s => (
                              <span key={s.id} style={styles.badge}>{s.name || s.id}</span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: '#aaa', fontSize: '0.85em' }}>Ninguna</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.statusBadge, 
                          backgroundColor: p.active ? '#e6f4ea' : '#fce8e6',
                          color: p.active ? '#1e8e3e' : '#d93025'
                        }}>
                          {p.active ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td style={styles.td}>
                         <div style={{ display: 'flex', gap: 10 }}>
                            <button 
                               onClick={(e) => handleToggleActive(p, e)}
                               style={{...styles.iconBtn, color: p.active ? '#d93025' : '#1e8e3e'}}
                               title={p.active ? 'Desactivar' : 'Activar'}
                            >
                               {p.active ? <AiOutlineStop /> : <AiOutlineCheck />}
                            </button>
                            <button 
                               onClick={(e) => handleDelete(p, e)}
                               style={{...styles.iconBtn, color: '#d93025'}}
                               title="Eliminar"
                            >
                               <AiOutlineDelete />
                            </button>
                         </div>
                      </td>
                    </tr>
                  ))}
                  {policies.length === 0 && (
                    <tr>
                      <td colSpan="5" style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                        No hay políticas de retención configuradas.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {isEditing && (
          <div style={styles.editSection}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>
                {selectedPolicy === 'new' ? 'Crear Política' : 'Editar Política Formulario'}
              </h2>
            </div>
            
            <div style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Nombre de la Política</label>
                <input 
                  type="text" 
                  style={styles.input} 
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="Ej: Facturas 5 años"
                />
              </div>

              <div style={{ display: 'flex', gap: 20 }}>
                <div style={{...styles.formGroup, flex: 1}}>
                  <label style={styles.label}>Valor del Período</label>
                  <input 
                    type="number" 
                    min="1"
                    style={styles.input} 
                    value={formData.retentionPeriodValue}
                    onChange={e => setFormData({...formData, retentionPeriodValue: e.target.value})}
                    placeholder="Ej: 5"
                  />
                </div>
                <div style={{...styles.formGroup, flex: 1}}>
                  <label style={styles.label}>Formato del Período</label>
                  <select
                    style={styles.input} 
                    value={formData.retentionPeriodUnit}
                    onChange={e => setFormData({...formData, retentionPeriodUnit: e.target.value})}
                  >
                    <option value="d">Días (d)</option>
                    <option value="m">Meses (m)</option>
                    <option value="y">Años (y)</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Evento de Inicio de Retención</label>
                <select 
                  style={styles.input} 
                  value={formData.retentionStartEvent}
                  onChange={e => setFormData({...formData, retentionStartEvent: e.target.value})}
                >
                  <option value="CREATED">Creación del Documento (CREATED)</option>
                  <option value="PUBLISHED">Publicación del Documento (PUBLISHED)</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: 20, marginTop: 15 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '14px' }}>
                  <input 
                    type="checkbox" 
                    checked={formData.requiresApproval}
                    onChange={e => setFormData({...formData, requiresApproval: e.target.checked})}
                  />
                  Requiere Aprobación para Eliminar
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '14px' }}>
                  <input 
                    type="checkbox" 
                    checked={formData.physicalDelete}
                    onChange={e => setFormData({...formData, physicalDelete: e.target.checked})}
                  />
                  Eliminación Física (Destrucción total)
                </label>
              </div>

              <div style={styles.actions}>
                <button style={styles.cancelBtn} onClick={() => { setIsEditing(false); setSelectedPolicy(null); }}>
                  Cancelar
                </button>
                <button style={styles.saveBtn} onClick={handleSave}>
                  <AiOutlineSave /> Guardar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    borderBottom: '1px solid #e0e0e0',
    paddingBottom: '15px'
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  titleIcon: {
    fontSize: '24px',
    color: '#00468B'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    color: '#333'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  listSection: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: '20px'
  },
  editSection: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: '20px',
    border: '1px solid #e0e0e0'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  },
  sectionTitle: {
    margin: 0,
    fontSize: '18px',
    color: '#444'
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '8px 15px',
    backgroundColor: '#00468B',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  tableContainer: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid #ddd',
    color: '#666',
    fontWeight: 'bold'
  },
  tr: {
    borderBottom: '1px solid #ddd',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  trSelected: {
    backgroundColor: '#f0f7ff'
  },
  td: {
    padding: '12px'
  },
  badge: {
    backgroundColor: '#e0e0e0',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    color: '#333'
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    padding: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  label: {
    fontWeight: 'bold',
    fontSize: '14px',
    color: '#555'
  },
  input: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px'
  },
  saveBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '8px 20px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  cancelBtn: {
    padding: '8px 15px',
    backgroundColor: '#f3f4f6',
    color: '#4b5563',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  errorMessage: {
    padding: '10px 15px',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '4px',
    marginBottom: '15px'
  }
};


